/**
 * ===================================================================
 *  SCHEDULE SERVICE - Frontend API Client
 * ===================================================================
 *
 * Frontend služba pro komunikaci s Schedule API.
 * Zajišťuje type-safe komunikaci mezi React frontendem a backend API.
 *
 * Features:
 * - Complete schedule CRUD operations
 * - Cron validation
 * - Execution management
 * - Error handling
 *
 * Author: GitHub Copilot Coding Agent
 * Created: 2024-06-10
 * ===================================================================
 */

import { apiClient } from './api';

// Base URL pro schedule endpoints
const SCHEDULE_BASE_URL = 'schedules';

// Typy pro frontend (zjednodušená verze backend modelů)
export type SchedulePriority = 'CRITICAL' | 'HIGH' | 'NORMAL' | 'LOW' | 'BACKGROUND';

export interface Schedule {
  id: number;
  deviceId: string;
  name: string;
  description?: string;
  cronExpression: string;
  timezone: string;
  taskType: string;
  taskParameters: Record<string, any>;
  priority: SchedulePriority;
  enabled: boolean;
  nextExecution?: Date;
  lastExecution?: Date;
  executionCount: number;
  failureCount: number;
  consecutiveFailures: number;
  maxExecutionTimeMs: number;
  maxFailures: number;
  createdAt: Date;
  updatedAt: Date;
}

// Backend uses snake_case and string dates
export interface BackendSchedule {
  id: number;
  device_id: string;
  name: string;
  description?: string;
  cron_expression: string;
  timezone: string;
  task_type: string;
  task_parameters: Record<string, any>;
  priority: SchedulePriority;
  enabled: boolean;
  next_execution?: string;
  last_execution?: string;
  execution_count: number;
  failure_count: number;
  consecutive_failures: number;
  max_execution_time_ms: number;
  max_failures: number;
  created_at: string;
  updated_at: string;
}

export interface CreateScheduleRequest {
  deviceId: string;
  name: string;
  description?: string;
  cronExpression: string;
  timezone?: string;
  taskType: string;
  taskParameters?: Record<string, any>;
  priority?: SchedulePriority;
  maxExecutionTimeMs?: number;
  maxFailures?: number;
  enabled?: boolean;
}

export interface UpdateScheduleRequest {
  name?: string;
  description?: string;
  cronExpression?: string;
  timezone?: string;
  taskType?: string;
  taskParameters?: Record<string, any>;
  priority?: SchedulePriority;
  maxExecutionTimeMs?: number;
  maxFailures?: number;
  enabled?: boolean;
}

export interface ScheduleListParams {
  page?: number;
  limit?: number;
  deviceId?: string;
  taskType?: string;
  enabled?: boolean;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface TaskDefinition {
  taskType: string;
  displayName: string;
  description?: string;
  category: string;
  parametersSchema: Record<string, any>;
  defaultParameters: Record<string, any>;
  estimatedDurationMs: number;
  requiresHardware: boolean;
  canRunParallel: boolean;
}

export interface CronValidationResult {
  valid: boolean;
  error?: string;
  nextExecution?: string | Date;
  description?: string;
}

export interface ScheduleStatistics {
  totalSchedules: number;
  activeSchedules: number;
  runningExecutions: number;
  pendingExecutions: number;
  successRate: number;
  averageExecutionTime: number;
}

export interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  data?: T;
  errors?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export const SCHEDULE_TASK_LABELS: Record<string, string> = {
  PUMP_CONTROL: 'Ovládání čerpadla',
  PUMP_CYCLE: 'Aeroponické cyklování',
  VALVE_CONTROL: 'Ovládání ventilu',
  DOSING: 'Dávkování živin',
  LIGHTING_CONTROL: 'Ovládání osvětlení',
  CALIBRATE_SENSOR: 'Kalibrace senzoru',
  SYSTEM_REBOOT: 'Restart zařízení',
  OTA_UPDATE: 'OTA aktualizace',
  TOGGLE_OUTPUT: 'Ovládání výstupu',
  SET_PWM: 'Nastavení výkonu',
  READ_SENSOR: 'Nepodporované čtení senzoru',
  SENSOR_READING: 'Nepodporované čtení senzoru',
  REPORT_GENERATION: 'Nepodporované generování reportu',
  CUSTOM_SCRIPT: 'Nepodporovaný vlastní skript',
};

export function getScheduleTaskDisplayName(taskType: string): string {
  return SCHEDULE_TASK_LABELS[taskType] || taskType;
}

export function getDefaultMaxFailuresForTask(taskType: string): number {
  const failuresMap: Record<string, number> = {
    PUMP_CONTROL: 3,
    PUMP_CYCLE: 2,
    VALVE_CONTROL: 3,
    DOSING: 2,
    LIGHTING_CONTROL: 3,
    CALIBRATE_SENSOR: 2,
    SYSTEM_REBOOT: 1,
    OTA_UPDATE: 1,
    TOGGLE_OUTPUT: 3,
    SET_PWM: 3,
  };

  return failuresMap[taskType] || 3;
}

/**
 * Service pro správu rozvrhů
 */
class ScheduleService {
  /**
   * ===================================================================
   * SCHEDULE CRUD OPERATIONS
   * ===================================================================
   */

  /**
   * Načte seznam rozvrhů s filtrováním
   */
  async getSchedules(params: ScheduleListParams = {}): Promise<PaginatedResponse<Schedule>> {
    const queryParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value.toString());
      }
    });

    // apiClient.get vraci primo data (response.data), ne cele response
    const response = await apiClient.get<ApiResponse<PaginatedResponse<BackendSchedule>>>(
      `${SCHEDULE_BASE_URL}?${queryParams.toString()}`,
    );

    if (!response.success) {
      throw new Error(response.message || 'Chyba pri nacitani rozvrhu');
    }

    // Konverze string datumů na Date objekty
    const schedules = response.data!.data.map((schedule: BackendSchedule) => ({
      id: schedule.id,
      deviceId: schedule.device_id,
      name: schedule.name,
      description: schedule.description,
      cronExpression: schedule.cron_expression,
      timezone: schedule.timezone,
      taskType: schedule.task_type,
      taskParameters: schedule.task_parameters,
      priority: schedule.priority,
      enabled: schedule.enabled,
      nextExecution: schedule.next_execution ? new Date(schedule.next_execution) : undefined,
      lastExecution: schedule.last_execution ? new Date(schedule.last_execution) : undefined,
      executionCount: schedule.execution_count,
      failureCount: schedule.failure_count,
      consecutiveFailures: schedule.consecutive_failures,
      maxExecutionTimeMs: schedule.max_execution_time_ms,
      maxFailures: schedule.max_failures,
      createdAt: new Date(schedule.created_at),
      updatedAt: new Date(schedule.updated_at),
    }));

    return {
      data: schedules,
      pagination: response.data!.pagination,
    };
  }

  /**
   * Načte konkrétní rozvrh podle ID
   */
  async getScheduleById(id: number): Promise<Schedule> {
    const response = await apiClient.get<ApiResponse<BackendSchedule>>(
      `${SCHEDULE_BASE_URL}/${id}`,
    );

    if (!response.success) {
      throw new Error(response.message);
    }

    const schedule = response.data!;
    return {
      id: schedule.id,
      deviceId: schedule.device_id,
      name: schedule.name,
      description: schedule.description,
      cronExpression: schedule.cron_expression,
      timezone: schedule.timezone,
      taskType: schedule.task_type,
      taskParameters: schedule.task_parameters,
      priority: schedule.priority,
      enabled: schedule.enabled,
      nextExecution: schedule.next_execution ? new Date(schedule.next_execution) : undefined,
      lastExecution: schedule.last_execution ? new Date(schedule.last_execution) : undefined,
      executionCount: schedule.execution_count,
      failureCount: schedule.failure_count,
      consecutiveFailures: schedule.consecutive_failures,
      maxExecutionTimeMs: schedule.max_execution_time_ms,
      maxFailures: schedule.max_failures,
      createdAt: new Date(schedule.created_at),
      updatedAt: new Date(schedule.updated_at),
    };
  }

  /**
   * Vytvoří nový rozvrh
   */
  async createSchedule(data: CreateScheduleRequest): Promise<Schedule> {
    // Backend očekává snake_case, transformujeme camelCase -> snake_case
    const backendPayload = {
      device_id: data.deviceId,
      name: data.name,
      description: data.description,
      cron_expression: data.cronExpression,
      timezone: data.timezone,
      task_type: data.taskType,
      task_parameters: data.taskParameters,
      priority: data.priority,
      max_execution_time_ms: data.maxExecutionTimeMs,
      max_failures: data.maxFailures,
      enabled: data.enabled,
    };

    console.log('📤 Creating schedule with payload:', backendPayload);

    try {
      // API vrací přímo backendový schedule objekt
      const schedule = await apiClient.post<BackendSchedule>(SCHEDULE_BASE_URL, backendPayload);

      console.log('📥 Schedule created:', schedule);

      // Validace že jsme dostali platný schedule
      if (!schedule || !schedule.id) {
        throw new Error('Invalid response from server');
      }

      return {
        id: schedule.id,
        deviceId: schedule.device_id,
        name: schedule.name,
        description: schedule.description,
        cronExpression: schedule.cron_expression,
        timezone: schedule.timezone,
        taskType: schedule.task_type,
        taskParameters: schedule.task_parameters,
        priority: schedule.priority,
        enabled: schedule.enabled,
        nextExecution: schedule.next_execution ? new Date(schedule.next_execution) : undefined,
        lastExecution: schedule.last_execution ? new Date(schedule.last_execution) : undefined,
        executionCount: schedule.execution_count,
        failureCount: schedule.failure_count,
        consecutiveFailures: schedule.consecutive_failures,
        maxExecutionTimeMs: schedule.max_execution_time_ms,
        maxFailures: schedule.max_failures,
        createdAt: new Date(schedule.created_at),
        updatedAt: new Date(schedule.updated_at),
      };
    } catch (error: any) {
      console.error('❌ Schedule creation failed:', error);
      console.error('❌ Error message:', error.message);
      console.error('❌ Error code:', error.code);
      throw error;
    }
  }

  /**
   * Aktualizuje existující rozvrh
   */
  async updateSchedule(id: number, data: UpdateScheduleRequest): Promise<Schedule> {
    // Backend očekává snake_case
    const backendPayload: any = {};
    if (data.name !== undefined) backendPayload.name = data.name;
    if (data.description !== undefined) backendPayload.description = data.description;
    if (data.cronExpression !== undefined) backendPayload.cron_expression = data.cronExpression;
    if (data.timezone !== undefined) backendPayload.timezone = data.timezone;
    if (data.taskType !== undefined) backendPayload.task_type = data.taskType;
    if (data.taskParameters !== undefined) backendPayload.task_parameters = data.taskParameters;
    if (data.priority !== undefined) backendPayload.priority = data.priority;
    if (data.maxExecutionTimeMs !== undefined)
      backendPayload.max_execution_time_ms = data.maxExecutionTimeMs;
    if (data.maxFailures !== undefined) backendPayload.max_failures = data.maxFailures;
    if (data.enabled !== undefined) backendPayload.enabled = data.enabled;

    // API vrací přímo backendový schedule objekt
    const schedule = await apiClient.put<BackendSchedule>(
      `${SCHEDULE_BASE_URL}/${id}`,
      backendPayload,
    );

    return {
      id: schedule.id,
      deviceId: schedule.device_id,
      name: schedule.name,
      description: schedule.description,
      cronExpression: schedule.cron_expression,
      timezone: schedule.timezone,
      taskType: schedule.task_type,
      taskParameters: schedule.task_parameters,
      priority: schedule.priority,
      enabled: schedule.enabled,
      nextExecution: schedule.next_execution ? new Date(schedule.next_execution) : undefined,
      lastExecution: schedule.last_execution ? new Date(schedule.last_execution) : undefined,
      executionCount: schedule.execution_count,
      failureCount: schedule.failure_count,
      consecutiveFailures: schedule.consecutive_failures,
      maxExecutionTimeMs: schedule.max_execution_time_ms,
      maxFailures: schedule.max_failures,
      createdAt: new Date(schedule.created_at),
      updatedAt: new Date(schedule.updated_at),
    };
  }

  /**
   * Odstraní rozvrh
   */
  async deleteSchedule(id: number): Promise<void> {
    console.log('🗑️ Deleting schedule:', id);
    // API vrací 200 OK bez body při úspěchu
    await apiClient.delete(`${SCHEDULE_BASE_URL}/${id}`);
    console.log('✅ Schedule deleted successfully');
  }

  /**
   * Povolí/zakáže rozvrh
   */
  async toggleSchedule(id: number, enabled: boolean): Promise<Schedule> {
    // API vrací přímo backendový schedule objekt
    const schedule = await apiClient.patch<BackendSchedule>(`${SCHEDULE_BASE_URL}/${id}/toggle`, {
      enabled,
    });

    return {
      id: schedule.id,
      deviceId: schedule.device_id,
      name: schedule.name,
      description: schedule.description,
      cronExpression: schedule.cron_expression,
      timezone: schedule.timezone,
      taskType: schedule.task_type,
      taskParameters: schedule.task_parameters,
      priority: schedule.priority,
      enabled: schedule.enabled,
      nextExecution: schedule.next_execution ? new Date(schedule.next_execution) : undefined,
      lastExecution: schedule.last_execution ? new Date(schedule.last_execution) : undefined,
      executionCount: schedule.execution_count,
      failureCount: schedule.failure_count,
      consecutiveFailures: schedule.consecutive_failures,
      maxExecutionTimeMs: schedule.max_execution_time_ms,
      maxFailures: schedule.max_failures,
      createdAt: new Date(schedule.created_at),
      updatedAt: new Date(schedule.updated_at),
    };
  }

  /**
   * ===================================================================
   * EXECUTION MANAGEMENT
   * ===================================================================
   */

  /**
   * Spustí rozvrh okamžitě
   */
  async executeSchedule(
    id: number,
    taskParameters?: Record<string, any>,
  ): Promise<{ executionId: string; scheduleId: number }> {
    console.log('▶️ Executing schedule:', id);
    // API vrací přímo data
    const result = await apiClient.post<{ executionId: string; scheduleId: number }>(
      `${SCHEDULE_BASE_URL}/${id}/execute`,
      { taskParameters },
    );
    console.log('✅ Execution started:', result);
    return result;
  }

  /**
   * ===================================================================
   * VALIDATION & UTILITIES
   * ===================================================================
   */

  /**
   * Validuje cron výraz
   */
  async validateCronExpression(
    expression: string,
    timezone: string = 'UTC',
  ): Promise<CronValidationResult> {
    const response = await apiClient.post<ApiResponse<CronValidationResult>>(
      `${SCHEDULE_BASE_URL}/validate-cron`,
      { expression, timezone },
    );

    if (response.success) {
      const result = response.data!;
      return {
        ...result,
        nextExecution: result.nextExecution ? new Date(result.nextExecution) : undefined,
      };
    } else {
      return {
        valid: false,
        error: response.message,
      };
    }
  }

  /**
   * Načte definice typů úloh
   */
  async getTaskDefinitions(): Promise<TaskDefinition[]> {
    const response = await apiClient.get<ApiResponse<TaskDefinition[]>>(
      `${SCHEDULE_BASE_URL}/task-definitions`,
    );

    if (!response.success) {
      throw new Error(response.message);
    }

    return response.data!;
  }

  /**
   * ===================================================================
   * STATISTICS & MONITORING
   * ===================================================================
   */

  /**
   * Načte celkové statistiky
   */
  async getStatistics(): Promise<ScheduleStatistics> {
    const response = await apiClient.get<ApiResponse<ScheduleStatistics>>(
      `${SCHEDULE_BASE_URL}/statistics`,
    );

    if (!response.success) {
      throw new Error(response.message);
    }

    return response.data!;
  }

  /**
   * Načte nadcházející rozvrhy
   */
  async getUpcomingSchedules(hours: number = 24): Promise<Schedule[]> {
    const response = await apiClient.get<ApiResponse<BackendSchedule[]>>(
      `${SCHEDULE_BASE_URL}/upcoming?hours=${hours}`,
    );

    if (!response.success) {
      throw new Error(response.message);
    }

    return response.data!.map((schedule: BackendSchedule) => ({
      id: schedule.id,
      deviceId: schedule.device_id,
      name: schedule.name,
      description: schedule.description,
      cronExpression: schedule.cron_expression,
      timezone: schedule.timezone,
      taskType: schedule.task_type,
      taskParameters: schedule.task_parameters,
      priority: schedule.priority,
      enabled: schedule.enabled,
      nextExecution: schedule.next_execution ? new Date(schedule.next_execution) : undefined,
      lastExecution: schedule.last_execution ? new Date(schedule.last_execution) : undefined,
      executionCount: schedule.execution_count,
      failureCount: schedule.failure_count,
      consecutiveFailures: schedule.consecutive_failures,
      maxExecutionTimeMs: schedule.max_execution_time_ms,
      maxFailures: schedule.max_failures,
      createdAt: new Date(schedule.created_at),
      updatedAt: new Date(schedule.updated_at),
    }));
  }

  /**
   * ===================================================================
   * HELPER METHODS
   * ===================================================================
   */

  /**
   * Generuje lidsky čitelný popis cron výrazu
   */
  generateCronDescription(cronExpression: string): string {
    const specialFormats: Record<string, string> = {
      '@yearly': 'Ročně (1. ledna o půlnoci)',
      '@annually': 'Ročně (1. ledna o půlnoci)',
      '@monthly': 'Měsíčně (1. den v měsíci o půlnoci)',
      '@weekly': 'Týdně (v neděli o půlnoci)',
      '@daily': 'Denně o půlnoci',
      '@midnight': 'Denně o půlnoci',
      '@hourly': 'Každou hodinu',
    };

    if (cronExpression.startsWith('@')) {
      return specialFormats[cronExpression] || 'Speciální rozvrh';
    }

    // Základní parsování standardního cron formátu
    const parts = cronExpression.split(' ');
    if (parts.length !== 5) {
      return `Vlastní rozvrh: ${cronExpression}`;
    }

    const [minute, hour, day, month, weekday] = parts;

    // Jednoduchý popis pro některé běžné vzory
    if (minute === '0' && hour !== '*' && day === '*' && month === '*' && weekday === '*') {
      return `Denně v ${hour}:00`;
    }

    if (minute !== '*' && hour !== '*' && day === '*' && month === '*' && weekday === '*') {
      return `Denně v ${hour}:${minute.padStart(2, '0')}`;
    }

    if (minute === '0' && hour === '0' && day === '*' && month === '*' && weekday !== '*') {
      const weekdays = ['neděle', 'pondělí', 'úterý', 'středa', 'čtvrtek', 'pátek', 'sobota'];
      if (/^\d$/.test(weekday)) {
        return `Týdně v ${weekdays[parseInt(weekday)]} o půlnoci`;
      }
    }

    return `Vlastní rozvrh: ${cronExpression}`;
  }

  /**
   * Vypočítá relativní čas do příštího spuštění
   */
  getTimeUntilExecution(nextExecution?: Date): string {
    if (!nextExecution) return 'Není naplánováno';

    const now = new Date();
    const diff = nextExecution.getTime() - now.getTime();

    if (diff < 0) return 'Po termínu';

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) {
      return `Za ${days} dní`;
    } else if (hours > 0) {
      return `Za ${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `Za ${minutes} minut`;
    } else {
      return 'Právě teď';
    }
  }

  /**
   * Vypočítá úspěšnost rozvrhu v procentech
   */
  calculateSuccessRate(executionCount: number, failureCount: number): number {
    if (executionCount === 0) return 100;
    return Math.round(((executionCount - failureCount) / executionCount) * 100);
  }

  /**
   * Formátuje stav rozvrhu
   */
  formatScheduleStatus(schedule: Schedule): {
    status: 'active' | 'disabled' | 'warning' | 'error';
    text: string;
    color: string;
  } {
    if (!schedule.enabled) {
      return {
        status: 'disabled',
        text: 'Zakázáno',
        color: 'text-gray-500',
      };
    }

    if (schedule.consecutiveFailures >= 3) {
      return {
        status: 'error',
        text: 'Opakovaná selhání',
        color: 'text-red-600',
      };
    }

    if (schedule.consecutiveFailures > 0) {
      return {
        status: 'warning',
        text: 'Varování',
        color: 'text-yellow-600',
      };
    }

    return {
      status: 'active',
      text: 'Aktivní',
      color: 'text-green-600',
    };
  }
}

// Export singleton instance
export const scheduleService = new ScheduleService();
export default scheduleService;
